import React from 'react';

import {
  applyCellScopeToRequest,
  getAvailableCellIds,
  readCellScope,
  writeCellScope,
} from '@/services/cellScope';

type CellScopeContextValue = {
  cellId: string;
  availableCellIds: string[];
  setCellId: (nextCellId: string) => void;
};

const CellScopeContext = React.createContext<CellScopeContextValue | null>(null);

export function CellScopeProvider({ children }: { children: React.ReactNode }) {
  const [cellId, setCellIdState] = React.useState<string>(() => readCellScope());
  const availableCellIds = React.useMemo(() => {
    const base = getAvailableCellIds();
    if (base.includes(cellId)) return base;
    return [...base, cellId];
  }, [cellId]);

  const activeCellRef = React.useRef(cellId);
  React.useEffect(() => {
    activeCellRef.current = cellId;
  }, [cellId]);

  React.useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const originalFetch = window.fetch.bind(window);
    window.fetch = ((input: RequestInfo | URL, init?: RequestInit) => {
      const [scopedInput, scopedInit] = applyCellScopeToRequest(
        input,
        init,
        activeCellRef.current
      );
      return originalFetch(scopedInput as RequestInfo | URL, scopedInit);
    }) as typeof window.fetch;

    return () => {
      window.fetch = originalFetch;
    };
  }, []);

  const setCellId = React.useCallback((nextCellId: string) => {
    setCellIdState(writeCellScope(nextCellId));
  }, []);

  const value = React.useMemo<CellScopeContextValue>(
    () => ({
      cellId,
      availableCellIds,
      setCellId,
    }),
    [cellId, availableCellIds, setCellId]
  );

  return <CellScopeContext.Provider value={value}>{children}</CellScopeContext.Provider>;
}

export function useCellScope(): CellScopeContextValue {
  const ctx = React.useContext(CellScopeContext);
  if (!ctx) {
    throw new Error('useCellScope must be used inside CellScopeProvider');
  }
  return ctx;
}
